import React, { useState } from 'react';
import { WorksmartAIMemberData, WorksmartTeamStats } from '../types/worksmartTypes';
import { getAIAdoptionLevel, formatHours } from '../services/worksmartAIService';
import ExportButtons from './ExportButtons';
import { exportWorksmartAIToCSV, exportWorksmartAIToPDF, WorksmartAIExportOptions } from '../services/worksmartAIExportService';
import './WorksmartAISection.css';

interface WorksmartAISectionProps {
  worksmartData: WorksmartAIMemberData[];
  teamStats: WorksmartTeamStats;
  loading: boolean;
  error: string | null;
  weekDate: string;
  onSortChange: (field: keyof WorksmartAIMemberData) => void;
  sortBy: keyof WorksmartAIMemberData;
  exportOptions?: WorksmartAIExportOptions;
}

const WorksmartAISection: React.FC<WorksmartAISectionProps> = ({
  worksmartData,
  teamStats,
  loading,
  error,
  weekDate,
  onSortChange,
  sortBy,
  exportOptions
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [minAIRatioFilter, setMinAIRatioFilter] = useState<number>(0);

  if (loading) {
    return (
      <div className="worksmart-ai-section">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Worksmart AI Data...</p>
          <small>Fetching AI usage metrics from Worksmart timecard tags</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="worksmart-ai-section">
        <div className="error-container">
          <h3>⚠️ Unable to Load Worksmart AI Data</h3>
          <p>{error}</p>
          <small>Worksmart AI data requires team access (Central PS or My Team mode)</small>
        </div>
      </div>
    );
  }

  // Filter data based on search term and minimum AI ratio
  const filteredData = worksmartData.filter(user => {
    const matchesSearch = !searchTerm || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesMinRatio = user.aiRatioPercent >= minAIRatioFilter;
    
    return matchesSearch && matchesMinRatio;
  });

  const handleSort = (field: keyof WorksmartAIMemberData) => {
    onSortChange(field);
  };

  const getSortIcon = (field: keyof WorksmartAIMemberData): string => {
    return sortBy === field ? '▼' : '▽';
  };

  /**
   * Handle CSV export
   */
  const handleExportCSV = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for Worksmart AI CSV export');
      return;
    }
    exportWorksmartAIToCSV(filteredData, exportOptions);
  };

  /**
   * Handle PDF export
   */
  const handleExportPDF = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for Worksmart AI PDF export');
      return;
    }
    exportWorksmartAIToPDF(filteredData, exportOptions);
  };

  const formatDateRange = (weekDate: string): string => {
    const start = new Date(weekDate);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    
    const startFormatted = start.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    const endFormatted = end.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const renderAIRatioCell = (user: WorksmartAIMemberData) => {
    const level = getAIAdoptionLevel(user.aiRatioPercent);
    const percentage = user.aiRatioPercent.toFixed(2);
    
    return (
      <div className="ai-ratio-cell">
        <div className="ai-ratio-bar-container">
          <div 
            className="ai-ratio-bar" 
            style={{ 
              width: `${Math.min(100, user.aiRatioPercent)}%`,
              backgroundColor: level.color
            }}
          />
        </div>
        <div className="ai-ratio-value" style={{ color: level.color }}>
          {percentage}%
        </div>
      </div>
    );
  };

  return (
    <div className="ai-overview-table-container">
      {/* Header matching other sections */}
      <div className="ai-overview-header">
        <div>
          <h3>📊 Worksmart AI Usage Report</h3>
          <p>Weekly AI tool usage based on Worksmart timecard tags (AI_USAGE, SECOND_BRAIN)</p>
        </div>
        
        {exportOptions && filteredData.length > 0 && (
          <ExportButtons
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
            disabled={filteredData.length === 0}
            className="worksmart-ai-export-container"
          />
        )}
      </div>

      {/* Stats section */}
      <div className="ai-overview-stats">
        <div className="stats-section">
          <div className="stat-item">
            <span className="stat-label">Team Members:</span>
            <span className="stat-value">{teamStats.totalUsers}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Week:</span>
            <span className="stat-value">{formatDateRange(weekDate)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg AI Ratio:</span>
            <span className="stat-value">
              {teamStats.averageAIRatioPercent.toFixed(2)}%
            </span>
          </div>
          {teamStats.highestAIUser && (
            <div className="stat-item">
              <span className="stat-label">Top AI User:</span>
              <span className="stat-value" style={{ color: '#22c55e' }}>
                {teamStats.highestAIUser.name} ({(teamStats.highestAIUser.aiRatio * 100).toFixed(1)}%)
              </span>
            </div>
          )}
          <div className="stat-item">
            <span className="stat-label">Showing:</span>
            <span className="stat-value">
              {filteredData.length} of {worksmartData.length} users
            </span>
          </div>
        </div>
      </div>

      {/* Filters and search */}
      <div className="worksmart-filters">
        <div className="filter-group">
          <label htmlFor="search">Search:</label>
          <input
            id="search"
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="minRatio">Min AI Ratio:</label>
          <select
            id="minRatio"
            value={minAIRatioFilter}
            onChange={(e) => setMinAIRatioFilter(Number(e.target.value))}
            className="filter-select"
          >
            <option value={0}>All Users</option>
            <option value={30}>≥ 30% (Low+)</option>
            <option value={50}>≥ 50% (Moderate+)</option>
            <option value={70}>≥ 70% (High)</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="sortBy">Sort by:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => handleSort(e.target.value as keyof WorksmartAIMemberData)}
            className="filter-select"
          >
            <option value="name">Name</option>
            <option value="aiRatioPercent">AI Ratio</option>
            <option value="aiHours">AI Hours</option>
            <option value="totalHours">Total Hours</option>
          </select>
        </div>
      </div>

      {/* Table wrapper */}
      <div className="table-wrapper">
        <table className="ai-overview-table worksmart-ai-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th 
                className={`sortable ${sortBy === 'name' ? 'sorted' : ''}`}
                onClick={() => handleSort('name')}
              >
                Team Member {getSortIcon('name')}
              </th>
              <th 
                className={`sortable ${sortBy === 'aiHours' ? 'sorted' : ''}`}
                onClick={() => handleSort('aiHours')}
              >
                AI Hours {getSortIcon('aiHours')}
              </th>
              <th 
                className={`sortable ${sortBy === 'totalHours' ? 'sorted' : ''}`}
                onClick={() => handleSort('totalHours')}
              >
                Total Hours {getSortIcon('totalHours')}
              </th>
              <th 
                className={`sortable ${sortBy === 'aiRatioPercent' ? 'sorted' : ''}`}
                onClick={() => handleSort('aiRatioPercent')}
              >
                AI Ratio {getSortIcon('aiRatioPercent')}
              </th>
              <th>Adoption Level</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((user, index) => {
              const level = getAIAdoptionLevel(user.aiRatioPercent);
              return (
                <tr key={user.xoUserId} className={`user-row ${index < 3 ? `rank-${index + 1}` : ''}`}>
                  <td className="rank-cell">
                    <span className="rank-number">{index + 1}</span>
                    {index === 0 && <span key="gold" className="rank-badge gold">🥇</span>}
                    {index === 1 && <span key="silver" className="rank-badge silver">🥈</span>}
                    {index === 2 && <span key="bronze" className="rank-badge bronze">🥉</span>}
                  </td>
                  <td className="team-member-cell">
                    <strong>{user.name}</strong>
                    {user.email && (
                      <>
                        <br />
                        <small style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>{user.email}</small>
                      </>
                    )}
                  </td>
                  <td className="metric-cell">
                    <span className="hours-value">{formatHours(user.aiHours)}</span>
                  </td>
                  <td className="metric-cell">
                    <span className="hours-value">{formatHours(user.totalHours)}</span>
                  </td>
                  <td className="ai-ratio-value-cell">
                    {renderAIRatioCell(user)}
                  </td>
                  <td className="adoption-level-cell">
                    <span 
                      className="adoption-badge"
                      style={{ 
                        backgroundColor: level.color,
                        color: 'white',
                        padding: '0.3rem 0.6rem',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                    >
                      {level.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredData.length === 0 && worksmartData.length > 0 && (
        <div className="no-data-message">
          No users match the current filters. Try adjusting your search or minimum AI ratio.
        </div>
      )}

      {worksmartData.length === 0 && (
        <div className="no-data-message">
          No Worksmart AI data available for the selected week.
        </div>
      )}

      {/* Legend */}
      <div className="ai-overview-stats" style={{ marginTop: '1rem' }}>
        <div className="stats-section">
          <div className="stat-item">
            <span className="stat-label">AI Adoption Levels:</span>
            <span className="stat-value">
              <span className="legend-badge" style={{ backgroundColor: '#22c55e', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>≥75% Leaders</span>
              <span className="legend-badge" style={{ backgroundColor: '#eab308', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>50-75% Consistent Progress</span>
              <span className="legend-badge" style={{ backgroundColor: '#f97316', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>30-50% Building Momentum</span>
              <span className="legend-badge" style={{ backgroundColor: '#ef4444', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px' }}>0-30% Getting Started</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorksmartAISection;


